import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationEnabled } from '../store/authSlice';
import { warningActions } from '../store/warningSlice';
import { isFieldRole, ROLES } from '../constants/status';
import { removeUserCoordinate, patchBreakStatus } from '../services/locationService';
import { getEventCoords } from '../services/lastKnownPosition'; // CLAUDE-SYNC: v1.7.20
import { geoWatchRef } from '../App'; // CLAUDE-SYNC: v1.4.1 B6 — needed to clear watch on off-duty

function TopBar() {
  const dispatch = useDispatch();
  const { role, user, isLocationEnabled } = useSelector((state) => state.auth);
  const [showModal, setShowModal] = useState(false);
  // CLAUDE-SYNC: v1.7 — guards against duplicate clock-in/out entries when a
  // request hangs on poor facility connectivity and the phleb taps again (2026-08-06)
  const [isToggling, setIsToggling] = useState(false);
  // CLAUDE-SYNC: v1.7.12 — break tracking. Local-only UI state (no GET
  // endpoint for "currently on break" yet); the underlying CheckIn record
  // is correct regardless, this just tracks the button label for this
  // session. Per Tanaya's Aug 26 call: a break behaves exactly like clock
  // in/out, just visually distinct.
  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isBreakToggling, setIsBreakToggling] = useState(false);

  const handleBreakToggle = async () => {
    if (isBreakToggling) return;
    const newValue = !isOnBreak;
    setIsBreakToggling(true);
    try {
      // CLAUDE-SYNC: v1.7.15 — capture optional GPS coordinates on break
      // start/end, same best-effort getCoordsSafe() pattern used for
      // Clock In/Out — never blocks the toggle if location isn't available.
      const coords = await getCoordsSafe();
      await patchBreakStatus(newValue, coords?.lat, coords?.lng);
      setIsOnBreak(newValue);
    } catch (err) {
      console.warn('Break toggle failed:', err?.message);
      dispatch(warningActions.showWarning({
        title: 'Could Not Update Break Status',
        message: "We couldn't reach the server to update your break status. Check your connection and try again.",
      }));
    } finally {
      setIsBreakToggling(false);
    }
  };
  const userInitials = user.name
    ? user?.name?.split(" ").map((l) => l[0]).join("").toUpperCase()
    : [user?.role?.split("")[0].toUpperCase(), user?.role?.split("")[5].toUpperCase()];

  // CLAUDE-SYNC: v1.7 — Clock In/Out reliability fix (2026-08-06)
  // Previously this dispatched setLocationEnabled() optimistically BEFORE the
  // server call resolved, with no timeout and no guard against a second tap.
  // On flaky facility connectivity the request could hang indefinitely while
  // the badge already showed the new status, so phlebs would tap again —
  // producing two real duty-toggle calls that both land once the connection
  // returns, creating duplicate clock-in/out pairs in the audit trail.
  // CLAUDE-SYNC: v1.7.20 — shared last-known-position helper (see services/lastKnownPosition.js)
  const getCoordsSafe = () => getEventCoords();
  
  const handleLocationToggle = async () => {
    if (isToggling) return;
    const newValue = !isLocationEnabled;
    setIsToggling(true);
    try {
      const coords = await getCoordsSafe();
      // Wait for server confirmation before changing anything locally.
      await removeUserCoordinate(newValue, coords);
      if (!newValue && geoWatchRef.id !== null) {
        navigator.geolocation.clearWatch(geoWatchRef.id);
        geoWatchRef.id = null;
      }
      dispatch(setLocationEnabled(newValue));
      setShowModal(false);
    } catch (err) {
      console.warn('Duty toggle failed:', err?.message);
      dispatch(warningActions.showWarning({
        title: 'Could Not Update Status',
        message: "We couldn't reach the server to update your clock status. Check your connection and try again — your status hasn't changed.",
      }));
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <>
      <div className="topbar" style={{ background: 'var(--rp)', padding: '8px 16px 15px', display: 'flex', alignItems: 'center', gap: 10, height: "8vh" }}>
        <div>
          <div style={{ fontFamily: "'Lora', serif", fontWeight: 600, color: 'white', fontSize: 15, letterSpacing: 0.3 }}>ReyaGo</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 300, marginTop: 1 }}>Specimen Logistics</div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>

          {/* Location toggle — only for field roles */}
          {isFieldRole(role) && (
            <button
              onClick={() => !isToggling && setShowModal(true)}
              disabled={isToggling}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                background: 'rgba(255,255,255,0.08)',
                border: '1.5px solid rgba(255,255,255,0.22)',
                borderRadius: 20,
                padding: '5px 14px',
                cursor: isToggling ? 'default' : 'pointer',
                opacity: isToggling ? 0.6 : 1,
                color: 'rgba(255,255,255,0.75)',
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                letterSpacing: 0.2,
              }}
            >
              <span style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: isLocationEnabled ? '#2ecc71' : 'rgba(255,255,255,0.45)',
                flexShrink: 0,
              }} />
              {isToggling ? 'Updating…' : (isLocationEnabled ? 'Clocked In' : 'Clocked Out')}
            </button>
          )}

          {/* Avatar */}
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--berry)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 11, color: 'white', fontWeight: 600,
          }}>
            {Array.isArray(userInitials) ? `${userInitials[0] ?? ""}${userInitials[1] ?? ""}` : userInitials}
          </div>
        </div>
      </div>


      {showModal && (
        <div
          onClick={() => !isToggling && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 16,
              width: '100%',
              maxWidth: 360,
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
          >
            {/* Header */}
            <div style={{
              background: 'var(--rp)',
              padding: '18px 20px 16px',
            }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'white', marginBottom: 4 }}>
                GPS & Location
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                Control when you appear on the Live Map
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* Status pill */}
              <div style={{
                background: isLocationEnabled ? '#F0FFF4' : '#FFF5F5',
                border: `1px solid ${isLocationEnabled ? '#C3E6CB' : '#F5C6CB'}`,
                borderRadius: 10,
                padding: '12px 14px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: isLocationEnabled ? '#27AE60' : '#E74C3C', marginBottom: 3 }}>
                  <span style={{
                    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                    background: isLocationEnabled ? '#27AE60' : '#E74C3C',
                    marginRight: 6, verticalAlign: 'middle',
                  }} />
                  {isLocationEnabled ? 'Clocked In' : 'Clocked Out'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--bodygray)' }}>
                  {isLocationEnabled
                    ? 'Your pin is visible on the Live Map'
                    : 'Your pin is hidden from the Live Map'}
                </div>
              </div>

              {/* Primary action button */}
              <button
                onClick={handleLocationToggle}
                disabled={isToggling}
                style={{
                  width: '100%', padding: '14px 0', borderRadius: 30, border: 'none',
                  background: isLocationEnabled ? '#C0392B' : 'var(--berry)',
                  fontSize: 14, fontWeight: 700, color: 'white',
                  cursor: isToggling ? 'default' : 'pointer',
                  opacity: isToggling ? 0.7 : 1,
                }}
              >
                {isToggling ? 'Updating…' : (isLocationEnabled ? 'Clock Out' : 'Clock In')}
              </button>

              {/* CLAUDE-SYNC: v1.7.12 — On Break / Off Break, same pattern as
                  Clock In/Out, only shown while clocked in. */}
              {isLocationEnabled && (
                <button
                  onClick={handleBreakToggle}
                  disabled={isBreakToggling}
                  style={{
                    width: '100%', padding: '14px 0', borderRadius: 30,
                    border: '1.5px solid #f59e0b',
                    background: isOnBreak ? '#FEF3E2' : 'white',
                    fontSize: 14, fontWeight: 700, color: '#b36a00',
                    cursor: isBreakToggling ? 'default' : 'pointer',
                    opacity: isBreakToggling ? 0.7 : 1,
                    marginTop: 10,
                  }}
                >
                  {isBreakToggling ? 'Updating…' : (isOnBreak ? 'Off Break' : 'On Break')}
                </button>
              )}

              {/* Cancel button */}
              <button
                onClick={() => setShowModal(false)}
                disabled={isToggling}
                style={{
                  width: '100%', padding: '13px 0', borderRadius: 30,
                  border: '1.5px solid #ddd', background: 'white',
                  fontSize: 14, fontWeight: 600, color: '#444',
                  cursor: isToggling ? 'default' : 'pointer',
                  opacity: isToggling ? 0.6 : 1,
                }}
              >
                Cancel
              </button>

              {/* Footer note */}
              <div style={{ fontSize: 11, color: 'var(--bodygray)', textAlign: 'center', lineHeight: 1.6 }}>
                You are automatically Clocked Out when you Sign Out.
                Your pin appears automatically when you open a stop.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TopBar;
